import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { Team, TeamSummary, CreateTeamRequest, UpdateTeamRequest } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';

interface UseTeamsReturn {
  teams: TeamSummary[];
  loading: boolean;
  error: string | null;
  fetchTeams: () => Promise<void>;
  getTeam: (teamId: string) => Promise<TeamSummary>;
  createTeam: (data: CreateTeamRequest) => Promise<TeamSummary>;
  updateTeam: (teamId: string, data: UpdateTeamRequest) => Promise<Team>;
  deleteTeam: (teamId: string) => Promise<void>;
}

export function useTeams(): UseTeamsReturn {
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await api.get<{ data: TeamSummary[] }>('/api/teams');
      setTeams(json.data || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  }, []);

  const getTeam = useCallback(async (teamId: string): Promise<TeamSummary> => {
    const json = await api.get<{ data: TeamSummary }>(`/api/teams/${teamId}`);
    return json.data;
  }, []);

  const createTeam = useCallback(async (data: CreateTeamRequest): Promise<TeamSummary> => {
    try {
      const json = await api.post<{ data: TeamSummary }>('/api/teams', data);
      setTeams((prev) => [json.data, ...prev]);
      toast.success('Team created');
      return json.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create team';
      toast.error(message);
      throw err;
    }
  }, []);

  const updateTeam = useCallback(async (teamId: string, data: UpdateTeamRequest): Promise<Team> => {
    try {
      const json = await api.patch<{ data: Team }>(`/api/teams/${teamId}`, data);
      setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, ...json.data } : t)));
      toast.success('Team updated');
      return json.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update team';
      toast.error(message);
      throw err;
    }
  }, []);

  const deleteTeam = useCallback(async (teamId: string): Promise<void> => {
    try {
      await api.del(`/api/teams/${teamId}`);
      setTeams((prev) => prev.filter((t) => t.id !== teamId));
      toast.success('Team deleted');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete team';
      toast.error(message);
      throw err;
    }
  }, []);

  return { teams, loading, error, fetchTeams, getTeam, createTeam, updateTeam, deleteTeam };
}
